import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Button,
  Box,
  Breadcrumbs,
  debounce,
  Link,
  Typography,
  IconButton,
  List,
  ListItem,
  useMediaQuery,
  ListItemIcon,
  alpha,
  MenuItem,
  Menu,
  Divider,
  styled,
  Tooltip,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Edit as EditIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Info,
  Warning,
} from '@mui/icons-material'

import { useTheme } from '@mui/material/styles/index.js'
import { useTranslation } from 'react-i18next'

import Constants from '../../../constants'
import { Route, Point, Mode } from '../../../models/commons.models'
import { Material, MaterialQuantity } from '../../../models/materials.models'
import { Plan } from '../../../models/catalogs.models'
import SegmentedControl from '../../common/button/SegmentedControl.button'
import SearchComponent from '../../common/input/Search.input'
import Stack from '../../common/Stack.common'
import useGesture from '../../../hooks/useGesture.hooks'
import { traduceCategory } from '../../../models/categories.models'
import { RoutePath } from '../../../hooks/useRoute.hooks'
import { StringUtils } from '../../../utils/commons.utils'
import { getCategoryChoices } from '../../../models/categories.models'
import { SelectOption } from '../../../models/props.models'

const SectionDivider = styled(Divider)(() => ({
  margin: '0 -15px',
}))

const SectionTitle = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: 600,
}))

const SectionText = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: 400,
}))

const Section = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '16px',
  paddingBottom: '16px',
  gap: '8px',
}))

type MaterialProps = {
  material: Material
  isSelected: boolean
  canUpdate: boolean
  isHidden: boolean
  onEdit: () => void
  onOpen: () => void
  onDuplicate: () => void
  onSelect: () => void
  onToogle: () => void
}

const MaterialComponent: React.FC<MaterialProps> = ({
  material,
  isSelected,
  canUpdate,
  isHidden,
  onSelect,
  onEdit,
  onOpen,
  onDuplicate,
  onToogle,
}): JSX.Element => {
  return (
    <ListItem
      id={`material.${material._id}`}
      onClick={onSelect}
      sx={{
        borderBottom: `1px solid ${Constants.colors.menuBorder}`,
        color: isSelected ? Constants.colors.primary : '',
      }}>
      <img
        id={`material.${material._id}.image`}
        src={
          material.mainImageFile?.src ||
          material.mainImageFile?.path ||
          Constants.resources.materialIcon
        }
        draggable={false}
        height="40px"
        width="40px"
        style={{ marginRight: '10px', objectFit: 'cover' }}
        alt=""
      />
      {material.name}

      <Box sx={{ marginLeft: 'auto', display: 'flex' }}>
        {canUpdate ? (
          <>
            <IconButton
              onClick={(e: any) => {
                onEdit()
                e.stopPropagation()
              }}
              size="small">
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={(e: any) => {
                onDuplicate()
                e.stopPropagation()
              }}
              size="small">
              <ContentCopyIcon />
            </IconButton>
          </>
        ) : (
          <IconButton
            onClick={(e: any) => {
              onOpen()
              e.stopPropagation()
            }}
            size="small">
            <EditIcon />
          </IconButton>
        )}
        <IconButton
          size="small"
          edge="end"
          disabled={material._id.includes('local')}
          onClick={(e: any) => {
            onToogle()
            e.stopPropagation()
          }}>
          {isHidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </Box>
    </ListItem>
  )
}

type CategoryProps = {
  level: number
  categoryId: number
  categorylabel: string
  hidden: boolean
  displayMode: 'icon' | 'code'
}

type PlanAsideProps = {
  plans: Plan[]
  canUpdate: boolean
  catalogId: string
  planId: string
  planName: string
  catalogName: string
  currentMaterial?: Material
  materials: Material[]
  hiddenCategories: number[]
  hiddenMaterials: string[]
  toogleOpenAside: boolean
  displayMode: 'icon' | 'code'
  setDisplayMode: (mode: 'icon' | 'code') => void
  goTo: (route: RoutePath) => void
  onSave: () => void
  select: (materialId?: string) => void
  onEdit: (material: Material) => void
  onOpen: (material: Material) => void
  onDuplicate: (material: Material) => void
  onDrag: (position: Point, material: Material) => void
  onDragEnd: () => void
  onDragCancel: () => void
  onToogle: (categoryId: number) => void
  onToogleMaterial: (materialId: string) => void
}

const PlanAside: React.FC<PlanAsideProps> = ({
  plans,
  catalogId,
  goTo,
  catalogName,
  planId,
  planName,
  currentMaterial,
  materials,
  hiddenCategories,
  hiddenMaterials,
  toogleOpenAside,
  canUpdate,
  displayMode,
  setDisplayMode,
  select,
  onEdit,
  onOpen,
  onDuplicate,
  onDrag,
  onDragEnd,
  onDragCancel,
  onToogle,
  onToogleMaterial,
  onSave,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const [draggingId, setDraggingId] = useState<string>('')
  const [open, setOpen] = useState<Record<number, number>>({
    0: 0,
    1: 0,
    2: 0,
  })
  const [asideOpened, setAsideOpened] = useState(false)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'plan'>('all')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const theme = useTheme()
  const onlyXs = useMediaQuery(theme.breakpoints.only('xs'))

  useEffect(() => {
    setAsideOpened(true)
  }, [toogleOpenAside])

  useEffect(() => {
    if (currentMaterial) {
      setOpen({
        0: currentMaterial.primaryCategory,
        1: currentMaterial.secondaryCategory,
        2: currentMaterial.tertiaryCategory,
      })

      setTimeout(() => {
        let scrollDiv = ref.current?.parentNode as HTMLElement
        if (scrollDiv) {
          const materialDiv = document.getElementById(`material.${currentMaterial._id}`)
          if (materialDiv) {
            scrollDiv.scrollTo({ top: materialDiv.offsetTop })
          }
        }
      }, 100)
    }
  }, [currentMaterial])

  useGesture({
    ref: ref as unknown as React.MutableRefObject<HTMLElement>,
    doubleClickLatency: 0,
    onDrag: (delta, position, target, isTouch) => {
      let scrollDiv = ref.current?.parentNode as HTMLElement
      if (isTouch && scrollDiv && (ref.current?.clientWidth || 0) > position[0]) {
        if (scrollDiv.scrollHeight > scrollDiv.clientHeight) {
          scrollDiv.scrollBy({ top: -delta[1] })
        }
      } else {
        if (!canUpdate) {
          return
        }
        if (draggingId) {
          const material = materials.find((material) => material._id === draggingId)
          if (material) {
            onDrag(position, material)
          }
        } else {
          const [type, ...splittedId] = (target?.id || '').split(/\./g)
          const id = splittedId.join('.')
          if (type === 'material' && id) {
            const material = materials.find((material) => material._id === id)
            if (material) {
              setDraggingId(id)
              onDrag(position, materials.find((material) => material._id === id) as Material)
              setAsideOpened(false)
            }
          }
        }
      }
    },
    onDragEnd: (position: Point) => {
      if (draggingId) {
        if ((ref.current?.clientWidth || 0) > position[0]) {
          onDragCancel()
        } else {
          onDragEnd()
        }
        setDraggingId('')
      }
    },
    onSingleClick: (_, target) => {
      if (target?.id.includes('unselect')) {
        select(undefined)
      }
    },
  })

  let filteredMaterials =
    filter === 'plan'
      ? materials.filter((material: Material) =>
          material.quantities.find(
            (materialQuantity: MaterialQuantity) => materialQuantity.plan?._id === planId,
          ),
        )
      : materials
  filteredMaterials = search
    ? materials.filter(
        (mat: Material) =>
          StringUtils.normalizeIncludes(mat.name || '', search) ||
          StringUtils.normalizeIncludes(mat.reference || '', search),
      )
    : filteredMaterials

  const isHidden = useCallback(
    (categoryId: number) => !!hiddenCategories.find((id) => id === categoryId),
    [hiddenCategories],
  )
  const isHiddenMaterial = useCallback(
    (materialId: string) => !!hiddenMaterials.find((id) => id === materialId),
    [hiddenMaterials],
  )

  const countMaterials = useCallback(
    (id: number): number =>
      filteredMaterials.filter((material: Material) => {
        return (
          material.primaryCategory === id ||
          material.secondaryCategory === id ||
          material.tertiaryCategory === id
        )
      }).length,
    [filteredMaterials],
  )

  const openCategory = useCallback(
    (level: number, categoryId: number) => {
      select(undefined)
      setOpen((currentsId) => {
        if (currentsId[level] === categoryId) {
          return {
            ...currentsId,
            [level]: 0,
            [level + 1]: 0,
            [level + 2]: 0,
          }
        } else {
          return {
            ...currentsId,
            [level]: categoryId,
            [level + 1]: 0,
            [level + 2]: 0,
          }
        }
      })
    },
    [select],
  )

  const CategoryComponent: React.FC<CategoryProps> = useCallback(
    ({ categoryId, categorylabel, level, hidden, displayMode }) => {
      const children = getCategoryChoices('rae', categoryId)

      let materialCount = countMaterials(categoryId)
      if (materialCount === 0) {
        return <></>
      }

      const categoryMaterials = filteredMaterials.filter(
        (material) => material.tertiaryCategory === categoryId,
      )

      const nextLevel: number | undefined = level === 2 ? undefined : level + 1

      const opened: any =
        open[level] === categoryId
          ? nextLevel
            ? children?.map((secondRecCat: SelectOption) => (
                <CategoryComponent
                  key={secondRecCat.value}
                  categoryId={secondRecCat.value}
                  categorylabel={secondRecCat.label}
                  level={nextLevel}
                  hidden={isHidden(secondRecCat.value)}
                  displayMode={displayMode}
                />
              ))
            : categoryMaterials.map((material: Material) => (
                <MaterialComponent
                  key={material._id}
                  material={material}
                  isSelected={currentMaterial?._id === material._id}
                  canUpdate={canUpdate}
                  isHidden={isHiddenMaterial(material._id)}
                  onToogle={() => onToogleMaterial(material._id)}
                  onOpen={() =>
                    onOpen(materials.find((mat) => mat._id === material._id) as Material)
                  }
                  onEdit={() =>
                    onEdit(materials.find((mat) => mat._id === material._id) as Material)
                  }
                  onDuplicate={() =>
                    onDuplicate(materials.find((mat) => mat._id === material._id) as Material)
                  }
                  onSelect={() => select(material._id)}
                />
              ))
          : null

      const padding = level === 2 ? '40px' : level === 1 ? '20px' : '0'
      return (
        <Box>
          <ListItem
            onClick={() => openCategory(level, categoryId)}
            sx={{ paddingLeft: padding, borderBottom: `1px solid ${Constants.colors.menuBorder}` }}>
            {level === 0 && displayMode === 'icon' && (
              <ListItemIcon>
                <img src={Constants.getCategoryIcon(categoryId)} alt="" />
              </ListItemIcon>
            )}
            {(level === 0 || level === 1) && displayMode === 'code' && (
              <ListItemIcon>
                <SectionText sx={{ fontWeight: 600, color: Constants.colors.primary }}>
                  {traduceCategory('rae', categoryId, true)}
                </SectionText>
              </ListItemIcon>
            )}
            <SectionText>
              {categorylabel} ({materialCount})
            </SectionText>
            <IconButton
              sx={{ marginLeft: 'auto' }}
              size="small"
              edge="end"
              onClick={(e: any) => {
                onToogle(categoryId)
                e.stopPropagation()
              }}>
              {hidden ? (
                <VisibilityOffIcon id={`toogle.${categoryId}`} />
              ) : (
                <VisibilityIcon id={`toogle.${categoryId}`} />
              )}
            </IconButton>
          </ListItem>
          {opened}
        </Box>
      )
    },
    [
      isHidden,
      isHiddenMaterial,
      openCategory,
      filteredMaterials,
      open,
      currentMaterial,
      countMaterials,
      onToogle,
      onToogleMaterial,
      select,
      onEdit,
      onOpen,
      canUpdate,
      onDuplicate,
      materials,
    ],
  )

  let info = ''
  if (materials.length === 0) {
    info = t('catalogs:components.asidePlan.noCatalogMaterials')
  } else if (filteredMaterials.length === 0) {
    info = search
      ? t('catalogs:components.asidePlan.noResults')
      : t('catalogs:components.asidePlan.noPlanMaterials')
  }

  const closedAside = (
    <IconButton size="large" onClick={() => setAsideOpened(true)}>
      <MenuIcon color="primary" />
    </IconButton>
  )
  const openAside = (
    <Box id="unselect" height="100%" display="flex" flexDirection="column" padding="0 15px">
      <Box
        position="sticky"
        top="0"
        sx={{ backgroundColor: Constants.colors.white, zIndex: 1 }}
        paddingTop="calc(25px + env(safe-area-inset-top))">
        <Breadcrumbs aria-label="breadcrumb">
          {onlyXs && (
            <IconButton size="large" onClick={() => setAsideOpened(false)}>
              <MenuIcon color="primary" />
            </IconButton>
          )}
          <Link
            underline="hover"
            color="inherit"
            onClick={goTo.bind(null, { route: Route.workspace })}>
            {t('global:routes.workspace')}
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={goTo.bind(null, { route: Route.workspaceCatalog, catalogId })}>
            {catalogName}
          </Link>
        </Breadcrumbs>

        <SectionDivider sx={{ mt: 1 }} />
        <Section>
          <SectionTitle>{t(`catalogs:components.asidePlan.plan`)}</SectionTitle>
          <Box display="flex">
            <Typography>{planName}</Typography>
            {plans?.length > 1 && (
              <KeyboardArrowDownIcon
                onClick={(evt: any) => {
                  if (plans?.length > 1) {
                    setAnchorEl(evt.target)
                  }
                }}
                sx={{ cursor: 'pointer', color: 'action.active' }}
              />
            )}
          </Box>

          <Menu
            onClick={(event) => event.stopPropagation()}
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={setAnchorEl.bind(null, null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
            {plans.map((plan, i) => (
              <MenuItem
                key={i}
                onClick={() => {
                  goTo({
                    route: Route.workspaceCatalogPlan,
                    catalogId: catalogId,
                    planId: plan._id,
                  })
                  setAnchorEl(null)
                }}>
                {plan.name}
              </MenuItem>
            ))}
          </Menu>
        </Section>
        <SectionDivider />
        <Section>
          <SectionTitle>{t(`catalogs:components.asidePlan.sectionTitles.display`)}</SectionTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SegmentedControl
              value={displayMode}
              onChange={(val) => setDisplayMode(val as 'icon' | 'code')}
              options={[
                { value: 'icon', label: t('catalogs:components.asidePlan.iconMode') },
                { value: 'code', label: t('catalogs:components.asidePlan.labelMode') },
              ]}
            />
            {displayMode === 'code' && (
              <Tooltip
                title={
                  <Box>
                    {t(`catalogs:components.asidePlan.tooltipCode.codeMeaning`)}
                    <br />
                    <Box display="flex" alignItems="center" gap={1}>
                      <Warning fontSize="small" />
                      {!Constants.getIsLocal()
                        ? t(`catalogs:components.asidePlan.tooltipCode.connected`)
                        : t(`catalogs:components.asidePlan.tooltipCode.notConnected`)}
                    </Box>
                  </Box>
                }>
                <Box display="flex">
                  <Info color="primary" />
                </Box>
              </Tooltip>
            )}
          </Box>
        </Section>
        <SectionDivider />
        <Section>
          <SectionTitle>
            {t(`catalogs:components.asidePlan.sectionTitles.materialList`)}
          </SectionTitle>
          <Box sx={{ mb: 1 }}>
            <SegmentedControl
              value={filter}
              onChange={(val) => setFilter(val as 'all' | 'plan')}
              options={[
                { value: 'all', label: 'Catalogue' },
                { value: 'plan', label: t('catalogs:components.asidePlan.plan') },
              ]}
            />
          </Box>
          <SearchComponent
            fullWidth
            onChange={debounce((value: any) => {
              setSearch(value)
            }, 400)}
            placeholder={t(`global:inputs.searchMaterialPlaceholder`)}
          />
        </Section>
      </Box>
      <Section flex={1}>
        <Stack display="flex" spacing={6}>
          {!search && (
            <List>
              {getCategoryChoices('rae').map((cat) => (
                <CategoryComponent
                  key={cat.value}
                  categoryId={cat.value}
                  categorylabel={cat.label}
                  level={0}
                  hidden={isHidden(cat.value)}
                  displayMode={displayMode}
                />
              ))}
            </List>
          )}
          {info && <Typography align="center">{info}</Typography>}
          {search &&
            filteredMaterials.map((material: Material) => (
              <MaterialComponent
                canUpdate={canUpdate}
                key={material._id}
                material={material}
                isHidden={isHiddenMaterial(material._id)}
                onToogle={() => onToogleMaterial(material._id)}
                isSelected={currentMaterial?._id === material._id}
                onOpen={() => onOpen(materials.find((mat) => mat._id === material._id) as Material)}
                onEdit={() => onEdit(materials.find((mat) => mat._id === material._id) as Material)}
                onDuplicate={() =>
                  onDuplicate(materials.find((mat) => mat._id === material._id) as Material)
                }
                onSelect={() => select(material._id)}
              />
            ))}
        </Stack>
      </Section>
      {Constants.mode !== Mode.app && canUpdate && (
        <Box
          position="sticky"
          bottom="0"
          paddingBottom="25px"
          sx={{ backgroundColor: Constants.colors.white, zIndex: 1 }}>
          <Button
            fullWidth
            variant="contained"
            size="small"
            sx={{ marginTop: 'auto' }}
            color="primary"
            onClick={onSave}>
            {t(`global:actions.save`)}
          </Button>
        </Box>
      )}
    </Box>
  )

  const containerStyle: React.CSSProperties =
    onlyXs && !asideOpened
      ? { position: 'fixed', top: 'calc(10px + env(safe-area-inset-top))', left: '10px' }
      : onlyXs && asideOpened
        ? {
            position: 'fixed',
            height: '100vh',
            width: `100vw`,
            background: alpha(Constants.colors.black, 0.5),
            borderRight: `1px solid ${Constants.colors.menuBorder}`,
          }
        : {
            height: '100vh',
            minWidth: `${Constants.ui.planAsideSize}px`,
            borderRight: `1px solid ${Constants.colors.menuBorder}`,
          }

  return (
    <div
      style={{
        ...containerStyle,
        overflowY: 'auto',
        overflowX: 'hidden',
        zIndex: onlyXs && asideOpened ? 10 : 1,
      }}
      onClick={() => setAsideOpened(false)}>
      <div
        ref={ref}
        style={{
          touchAction: 'none',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          background: 'white',
          width: onlyXs && asideOpened ? `${Constants.ui.planAsideSize}px` : '',
        }}
        onClick={(e: any) => e.stopPropagation()}>
        {onlyXs && !asideOpened && closedAside}
        {(!onlyXs || asideOpened) && openAside}
      </div>
    </div>
  )
}
export default PlanAside
